export type CaseStudy = {
  slug: string;
  kind: string;
  title: string;
  shortTitle: string;
  summary: string;
  image: string;
  imageAlt: string;
  tone: "forest" | "mint" | "paper";
  tags: string[];
  challenge: string;
  response: string;
  delivered: string[];
  proof: string[];
};

export const caseStudies: CaseStudy[] = [
  {
    slug: "hello-marky-chat",
    kind: "Flagship product case study",
    title: "Building a conversation product, not another chatbot skin.",
    shortTitle: "Hello Marky Chat",
    summary:
      "A real multi-tenant product for website, Messenger, Instagram, and Shopify conversations, with knowledge training and human handoff built into the operating model.",
    image: "/portfolio/hello-marky-inbox-moment.webp",
    imageAlt: "A business owner reviewing customer conversations on her phone",
    tone: "forest",
    tags: ["Product strategy", "AI systems", "SaaS", "Conversation design"],
    challenge:
      "Most chatbot tools stop at a generated answer. The harder problem is keeping the answer useful, the business context intact, and a person close enough to step in.",
    response:
      "Hello Marky Chat was built as a complete conversation operating layer: knowledge, channels, contact context, handoff, billing, permissions, and observable AI runs in one product.",
    delivered: [
      "A multi-tenant agent and knowledge-base system",
      "Embeddable web chat plus Messenger and Instagram channels",
      "Human takeover with conversation history attached",
      "Admin traces, usage visibility, permissions, and billing",
    ],
    proof: ["Working product repository", "Interactive channel demo", "Operational admin console"],
  },
  {
    slug: "conversation-commerce",
    kind: "Commerce capability lab",
    title: "Turning a product question into a clear path to checkout.",
    shortTitle: "Conversation commerce",
    summary:
      "A Shopify-aware conversation flow that can find products, explain options, collect variants, build a cart, and keep the shopper moving without hiding the human team.",
    image: "/portfolio/demo-weekend-carryall.webp",
    imageAlt: "Olive canvas Weekend Carryall used in the commerce conversation demo",
    tone: "mint",
    tags: ["Shopify", "Product discovery", "Cart actions", "Interaction design"],
    challenge:
      "A shopper rarely asks in catalogue language. They describe a need, a size, a deadline, or a concern. A useful system has to translate that conversation into an accurate commerce action.",
    response:
      "The product demo connects conversational intent to live Shopify capabilities, then presents products and variant choices inside the chat before handing checkout back to the store.",
    delivered: [
      "Product discovery inside a conversation",
      "Variant and quantity selection",
      "Cart creation and checkout handoff",
      "A replayable product demonstration for sales and onboarding",
    ],
    proof: ["Interactive product picker", "Shopify tool integration", "Multi-channel flow"],
  },
  {
    slug: "connected-handoff",
    kind: "Systems execution",
    title: "Keeping context intact when automation hands work to a person.",
    shortTitle: "Connected handoff",
    summary:
      "A practical system for capturing intent, contact details, conversation history, and the next useful action before a team member joins the exchange.",
    image: "/portfolio/hello-marky-workflow-session.webp",
    imageAlt: "Two business operators mapping a customer workflow together",
    tone: "paper",
    tags: ["Workflow design", "Lead routing", "Human handoff", "Operations"],
    challenge:
      "Automation creates more work when it loses the details a person needs. Customers repeat themselves, teams search across tabs, and the handoff arrives without a clear next step.",
    response:
      "The handoff model treats context as a deliverable. The system records the request, the useful history, the customer details, and the reason a person needs to join.",
    delivered: [
      "Structured lead and intent capture",
      "Conversation summaries for the receiving team",
      "Owner notifications and configurable webhooks",
      "Audit and activity trails for operational visibility",
    ],
    proof: ["Working handoff flow", "Contact context model", "Auditable activity trail"],
  },
];

export const services = [
  {
    title: "Custom systems",
    promise: "When off-the-shelf software keeps asking the business to bend.",
    body: "We design internal tools, portals, operating layers, and customer-facing products around the way the work actually moves.",
    deliverables: ["Internal platforms", "Client portals", "Operational dashboards", "Product builds"],
  },
  {
    title: "AI-powered automation",
    promise: "Repeated decisions and handoffs, handled with clear boundaries.",
    body: "We connect the tools already in use, add judgement where it is useful, and make failure states and human review part of the design.",
    deliverables: ["Lead routing", "Document workflows", "Follow-up systems", "Reporting flows"],
  },
  {
    title: "Chat and Voice AI",
    promise: "A helpful first response without pretending people are optional.",
    body: "We build customer conversation systems that answer, qualify, book, route, and bring a person in with the context intact.",
    deliverables: ["Website chat", "Social conversations", "Voice agents", "Human handoff"],
  },
];

export const productCapabilities = [
  "Website knowledge learning",
  "Incremental knowledge training",
  "Messenger and Instagram channels",
  "Shopify-aware product conversations",
  "Contact capture with custom fields",
  "Human takeover with full context",
  "AI run tracing and operational visibility",
  "Permissions, billing, and team management",
];
